import React, { useRef, useImperativeHandle, forwardRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  GraphicComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { IconButton, Tooltip } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import NoDataMessage from '../NoDataMessage/NoDataMessage';

import useChartOption from './useChartOption';
import {
  PanelWrapper,
  PanelHeader,
  PanelTitle,
  PanelActions,
  MinMaxRow,
  MinMaxLabel,
  DataTableWrapper,
  FullscreenOverlay,
  FullscreenPanel,
  FullscreenHeader,
  FullscreenBody,
} from './ChartPanelStyle';

// Register ECharts modules (tree-shaken)
echarts.use([
  LineChart,
  BarChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  GraphicComponent,
  CanvasRenderer,
]);

/* ── Data Table sub-component ── */
const DataTable = ({ data }) => (
  <DataTableWrapper>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Nombre</th>
          <th>Valor</th>
        </tr>
      </thead>
      <tbody>
        {data.map((d, i) => (
          <tr key={i}>
            <td>{i + 1}</td>
            <td>{d.name}</td>
            <td>{typeof d.value === 'number' ? d.value.toLocaleString('en-US', { maximumFractionDigits: 3 }) : d.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </DataTableWrapper>
);

/**
 * ChartPanel — ECharts-based chart card.
 *
 * Props (backward-compatible with ChartCard data shape):
 *   title, data, type, color, colors, barLayout, icon, min, max, size, wide, height, onExport
 */
const ChartPanel = forwardRef(function ChartPanel(
  {
    title,
    data = [],
    type = 'line',
    color,
    colors,
    barLayout = 'horizontal',
    icon,
    min,
    max,
    height,
    xAxisLabel,
    yAxisLabel,
    activityLabel,
    onExport,
    children,
  },
  ref
) {
  const chartRef = useRef(null);
  const fullChartRef = useRef(null);
  const [showTable, setShowTable] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const chartOption = useChartOption(type, data, { color, colors, barLayout, xAxisLabel, yAxisLabel });

  // Expose getDataURL for parent batch-export
  useImperativeHandle(ref, () => ({
    getDataURL: (opts) =>
      chartRef.current
        ?.getEchartsInstance()
        ?.getDataURL({ type: 'png', pixelRatio: 3, backgroundColor: '#fff', ...opts }),
  }));

  const hasData = chartOption !== null;
  const formatLimitValue = (v) =>
    v !== 'N/A' ? Number(v).toFixed(2) : 'N/A';

  /* ── PNG download ── */
  const handleDownloadPng = useCallback(() => {
    const target = isFullscreen ? fullChartRef.current : chartRef.current;
    const instance = target?.getEchartsInstance();
    if (!instance) return;
    const url = instance.getDataURL({ type: 'png', pixelRatio: 3, backgroundColor: '#fff' });
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title || 'chart'}.png`;
    link.click();
  }, [title, isFullscreen]);

  /* ── Excel download ── */
  const handleDownloadExcel = useCallback(async () => {
    if (!data || data.length === 0) return;

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'ODDISMAP';
      workbook.created = new Date();

      const sheet = workbook.addWorksheet('Datos');

      const DARK_BG = 'FF2d3748';
      const ACCENT_BG = 'FFff9900';
      const WHITE_FONT = 'FFFFFFFF';
      const LIGHT_STRIPE = 'FFf7f7f7';

      /* — Row 1: ODDISMAP title — */
      sheet.mergeCells('A1:C1');
      const titleCell = sheet.getCell('A1');
      titleCell.value = 'ODDISMAP';
      titleCell.font = { size: 16, bold: true, color: { argb: WHITE_FONT } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK_BG } };
      sheet.getRow(1).height = 30;

      /* — Row 2: Activity label — */
      if (activityLabel) {
        sheet.mergeCells('A2:C2');
        const actCell = sheet.getCell('A2');
        actCell.value = activityLabel;
        actCell.font = { size: 11, bold: true, color: { argb: WHITE_FONT } };
        actCell.alignment = { horizontal: 'center', vertical: 'middle' };
        actCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ACCENT_BG } };
      }

      /* — Row 3: Date and time — */
      const now = new Date();
      const dateStr = now.toLocaleDateString('es-GT', { year: 'numeric', month: 'long', day: 'numeric' });
      const timeStr = now.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' });
      sheet.mergeCells('A3:C3');
      const dateCell = sheet.getCell('A3');
      dateCell.value = `Fecha: ${dateStr}  —  Hora: ${timeStr}`;
      dateCell.font = { size: 10, italic: true };
      dateCell.alignment = { horizontal: 'center', vertical: 'middle' };

      /* — Row 4: empty — */
      sheet.addRow([]);

      /* — Row 5: Chart title — */
      sheet.mergeCells('A5:C5');
      const chartTitleCell = sheet.getCell('A5');
      chartTitleCell.value = title || 'Datos del gráfico';
      chartTitleCell.font = { size: 13, bold: true };
      chartTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      chartTitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe8e8e8' } };
      sheet.getRow(5).height = 24;

      /* — Row 6: empty — */

      /* — Row 7: Table headers — */
      const colName = xAxisLabel || 'Nombre';
      const colValue = yAxisLabel || 'Valor';
      const headerRow = sheet.getRow(7);
      headerRow.values = ['#', colName, colValue];
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: WHITE_FONT } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK_BG } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          bottom: { style: 'thin', color: { argb: 'FF999999' } },
        };
      });
      headerRow.height = 22;

      /* — Data rows — */
      data.forEach((d, i) => {
        const row = sheet.getRow(8 + i);
        const numVal = typeof d.value === 'number' ? d.value : d.value;
        row.values = [i + 1, d.name, numVal];
        row.getCell(1).alignment = { horizontal: 'center' };
        row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
        row.getCell(3).alignment = { horizontal: 'right', vertical: 'middle' };
        if (typeof numVal === 'number') {
          row.getCell(3).numFmt = '#,##0.00';
        }
        // Alternating row color
        if (i % 2 === 1) {
          row.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: LIGHT_STRIPE } };
          });
        }
        row.height = 18;
      });

      /* — Auto-fit column widths — */
      const colMaxWidths = [3, String(colName).length, String(colValue).length];
      data.forEach((d, i) => {
        colMaxWidths[0] = Math.max(colMaxWidths[0], String(i + 1).length);
        colMaxWidths[1] = Math.max(colMaxWidths[1], String(d.name || '').length);
        const valStr = typeof d.value === 'number' ? d.value.toFixed(2) : String(d.value || '');
        colMaxWidths[2] = Math.max(colMaxWidths[2], valStr.length);
      });
      colMaxWidths.forEach((maxLen, idx) => {
        sheet.getColumn(idx + 1).width = Math.min(Math.max(maxLen + 4, 8), 50);
      });

      /* — Freeze header row — */
      sheet.views = [{ state: 'frozen', ySplit: 7 }];

      /* — AutoFilter on data table — */
      const lastDataRow = 7 + data.length;
      sheet.autoFilter = { from: { row: 7, column: 1 }, to: { row: lastDataRow, column: 3 } };

      /* — Write and save — */
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${title || 'datos'}.xlsx`);
    } catch (err) {
      console.error('Error generating Excel:', err);
    }
  }, [data, title, activityLabel, xAxisLabel, yAxisLabel]);

  /* ── Legacy export callback ── */
  const handleExport = useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance();
    if (instance && onExport) {
      onExport(instance.getDataURL({ type: 'png', pixelRatio: 3, backgroundColor: '#fff' }));
    }
  }, [onExport]);

  /* ── Action buttons (reused in normal & fullscreen) ── */
  const renderActions = (isFull) => (
    <>
      {hasData && (
        <Tooltip title={showTable ? 'Ver gráfica' : 'Ver tabla'}>
          <IconButton size="small" onClick={() => setShowTable((p) => !p)}>
            {showTable ? <BarChartIcon fontSize="small" /> : <TableChartOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
      {hasData && (
        <Tooltip title="Descargar PNG">
          <IconButton size="small" onClick={handleDownloadPng}>
            <ImageOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {hasData && data.length > 0 && (
        <Tooltip title="Descargar Excel">
          <IconButton size="small" onClick={handleDownloadExcel}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!isFull && hasData && (
        <Tooltip title="Expandir">
          <IconButton size="small" onClick={() => setIsFullscreen(true)}>
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {isFull && (
        <Tooltip title="Cerrar">
          <IconButton size="small" onClick={() => setIsFullscreen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onExport && !isFull && (
        <Tooltip title="Exportar imagen">
          <IconButton size="small" onClick={handleExport}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  /* ── MinMax row (reused) ── */
  const renderMinMax = () =>
    hasData && min !== undefined && max !== undefined ? (
      <MinMaxRow>
        <MinMaxLabel color="#1b5e20">
          <ArrowDownwardIcon />
          Mín. {formatLimitValue(min)}
        </MinMaxLabel>
        <MinMaxLabel color="#B22222">
          <ArrowUpwardIcon />
          Máx. {formatLimitValue(max)}
        </MinMaxLabel>
      </MinMaxRow>
    ) : null;

  /* ── Fullscreen portal ── */
  const fullscreenPortal = isFullscreen
    ? ReactDOM.createPortal(
        <FullscreenOverlay onClick={(e) => { if (e.target === e.currentTarget) setIsFullscreen(false); }}>
          <FullscreenPanel onClick={(e) => e.stopPropagation()}>
            <FullscreenHeader>
              <PanelTitle>
                {icon && <span style={{ marginRight: 6, verticalAlign: 'middle' }}>{icon}</span>}
                {title}
              </PanelTitle>
              <PanelActions>{renderActions(true)}</PanelActions>
            </FullscreenHeader>
            <FullscreenBody>
              {showTable ? (
                <DataTable data={data} />
              ) : hasData ? (
                <ReactEChartsCore
                  ref={fullChartRef}
                  echarts={echarts}
                  option={chartOption}
                  theme="geomotica"
                  style={{ height: '60vh', width: '100%' }}
                  opts={{ renderer: 'canvas' }}
                  notMerge
                />
              ) : (
                <NoDataMessage message="No hay datos" />
              )}
              {!showTable && renderMinMax()}
            </FullscreenBody>
          </FullscreenPanel>
        </FullscreenOverlay>,
        document.body
      )
    : null;

  return (
    <PanelWrapper>
      {/* Header */}
      <PanelHeader>
        <PanelTitle>
          {icon && <span style={{ marginRight: 6, verticalAlign: 'middle' }}>{icon}</span>}
          {title}
        </PanelTitle>
        <PanelActions>{renderActions(false)}</PanelActions>
      </PanelHeader>

      {/* Chart / Table / empty state */}
      {showTable ? (
        <DataTable data={data} />
      ) : hasData ? (
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={chartOption}
          theme="geomotica"
          style={{ height: height || 320, width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge
        />
      ) : (
        <NoDataMessage message="No hay datos" />
      )}

      {/* Min / Max */}
      {!showTable && renderMinMax()}

      {/* Overlay slot */}
      {children}

      {/* Fullscreen modal */}
      {fullscreenPortal}
    </PanelWrapper>
  );
});

export default React.memo(ChartPanel);
